import React, { useCallback, useMemo, useState } from 'react';
import {
  FaBell,
  FaCheck,
  FaEnvelopeOpenText,
  FaExternalLinkAlt,
  FaRegCircle,
  FaRegDotCircle
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';

const formatRelativeTime = (isoString) => {
  if (!isoString) return '';
  const now = Date.now();
  const time = new Date(isoString).getTime();
  const diff = Math.max(now - time, 0);
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
  return new Date(isoString).toLocaleDateString();
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    notifications,
    unreadCount,
    loading,
    handleMarkRead,
    handleMarkUnread,
    handleMarkAllRead,
    refreshNotifications
  } = useNotifications();

  const [isOpen, setIsOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const handleToggle = useCallback((nextShow) => {
    setIsOpen(nextShow);
    if (nextShow) refreshNotifications();
  }, [refreshNotifications]);

  const closeDropdown = useCallback(() => setIsOpen(false), []);
  const openModal = useCallback(() => {
    closeDropdown();
    refreshNotifications();
    setShowModal(true);
  }, [closeDropdown, refreshNotifications]);
  const closeModal = useCallback(() => setShowModal(false), []);

  const ticketNavigationPath = useCallback((ticketId) => {
    if (!ticketId || !user) return null;
    if (user.role === 0) return `/admin/support-tickets?ticketId=${ticketId}`;
    if (user.role === 2) return `/organization/support-tickets?ticketId=${ticketId}`;
    return `/user/support-tickets/${ticketId}`;
  }, [user]);

  const handleNotificationClick = useCallback(async (notification) => {
    if (!notification) return;
    if (!notification.isRead) await handleMarkRead(notification.notificationId);
    const ticketId = notification.metadata?.ticketId || notification.metadata?.ticket_id;
    const destination = ticketNavigationPath(ticketId);
    if (destination) navigate(destination);
    closeDropdown();
  }, [closeDropdown, handleMarkRead, navigate, ticketNavigationPath]);

  const hasNotifications = notifications.length > 0;

  const dropdownContent = useMemo(() => {
    if (loading) {
      return (
        <div className="flex flex-col items-center gap-4 px-6 py-8" role="status" aria-live="polite">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-500 border-t-transparent"></div>
          <div className="text-center text-gray-400 text-sm">Loading notifications…</div>
        </div>
      );
    }

    if (!hasNotifications) {
      return (
        <div className="flex flex-col items-center gap-4 px-8 py-10 text-center">
          <div className="rounded-full bg-white/10 backdrop-blur-sm p-6 flex items-center justify-center">
            <FaEnvelopeOpenText className="text-gray-400 text-4xl" aria-hidden="true" />
          </div>
          <p className="mb-0 font-bold text-gray-300 text-lg">No notifications yet</p>
          <small className="text-gray-500">Support ticket updates will appear here.</small>
        </div>
      );
    }

    return (
      <div className="max-h-96 overflow-y-auto">
        {notifications.map((notification) => {
          const isUnread = !notification.isRead;
          const ticketId = notification.metadata?.ticketId || notification.metadata?.ticket_id;
          const destination = ticketNavigationPath(ticketId);

          return (
            <div
              key={notification.notificationId}
              className={`flex items-start gap-4 px-3 py-2 border-b border-white/10 ${isUnread ? 'bg-white/10' : 'bg-transparent'} hover:bg-white/5 transition-all`}
            >
              <button
                className={`mt-1 p-0 rounded-full flex items-center justify-center w-9 h-9 border-2 ${isUnread ? 'border-green-400 text-green-400' : 'border-gray-500 text-gray-500'} hover:scale-110 transition-all`}
                onClick={() => isUnread ? handleMarkRead(notification.notificationId) : handleMarkUnread(notification.notificationId)}
                aria-label={isUnread ? 'Mark as read' : 'Mark as unread'}
              >
                {isUnread ? <FaRegCircle /> : <FaRegDotCircle />}
              </button>

              <div className="flex-1 min-w-0">
                <button
                  className="text-left text-decoration-none w-full"
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className={`font-bold ${isUnread ? 'text-gray-800' : 'text-gray-700'}`}>{notification.title}</div>
                </button>
                <div className="text-gray-400 text-sm mt-1" style={{ whiteSpace: 'pre-line' }}>
                  {notification.body}
                </div>
                <div className="flex items-center gap-3 mt-3 text-gray-500 text-xs">
                  <span>{formatRelativeTime(notification.createdAt)}</span>
                  {destination && (
                    <span className="bg-white/10 text-gray-700 px-3 py-1 rounded-full flex items-center gap-1 text-xs font-medium">
                      View ticket
                      <FaExternalLinkAlt className="text-xs" />
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [handleMarkRead, handleMarkUnread, handleNotificationClick, hasNotifications, loading, notifications, ticketNavigationPath]);

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        onClick={() => handleToggle(!isOpen)}
        className="relative p-2 rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20 transition-all group"
        aria-label={unreadCount ? `${unreadCount} unread notifications` : 'Notifications'}
      >
        <FaBell className="text-white text-2xl group-hover:animate-bell-ring" size={24} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs font-bold rounded-full min-w-4 h-4 flex items-center justify-center shadow-lg animate-pulse">
            {Math.min(unreadCount, 99)}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={closeDropdown}></div>
          <div className="absolute right-0 mt-3 w-96 bg-white backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden z-50 text-black">
            {/* Header */}
            <div className="brand-app-notification-header p-3">
              <div className="flex items-center gap-3">
                <FaBell className="text-white/80 text-xl" />
                <span className="font-bold text-white text-lg">Notifications</span>
                {unreadCount > 0 && (
                  <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm font-bold">
                    {unreadCount} unread
                  </span>
                )}
              </div>
              {/* {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-all"
                  title="Mark all as read"
                >
                  <FaCheck className="text-green-400" />
                </button>
              )} */}
            </div>

            {/* Content */}
            {dropdownContent}

            {/* Footer */}
            <div className="px-5 py-4 border-t border-white/10 bg-white/5 text-right">
              <button
                onClick={openModal}
                className="text-[var(--brand-red)] hover:text-[var(--brand-red-dark)] font-bold text-sm transition-all"
              >
                See all notifications →
              </button>
            </div>
          </div>
        </>
      )}

      {/* Full Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ display: 'flex' }}>
          <div className="absolute inset-0 bg-black/40" onClick={closeModal}></div>
          <div className="relative w-full max-w-4xl bg-white rounded-3xl shadow-2xl overflow-hidden" style={{ maxHeight: '90vh', margin: 'auto' }}>
            {/* Modal Header */}
            <div className="brand-app-modal-header py-3 px-4 sm:px-4 sm:py-4 modal-header flex items-center justify-between">
              <h3 className="font-bold text-xl text-white mb-0">All Notifications</h3>
              <button 
                type="button" 
                onClick={closeModal} 
                className="btn-close btn-close-white" 
                aria-label="Close"
                style={{ filter: 'invert(1)' }}
              ></button>
            </div>

            {/* Modal Body */}
            <div className="p-0 bg-gray-50" style={{ maxHeight: 'calc(90vh - 140px)', overflowY: 'auto' }}>
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center gap-6 px-8 py-16 text-center bg-gray-50">
                  <div className="rounded-full bg-purple-100 p-8">
                    <FaEnvelopeOpenText className="text-purple-400 text-6xl" />
                  </div>
                  <p className="text-2xl font-bold text-gray-800">No notifications yet</p>
                  <small className="text-gray-600">New alerts will appear here once they are available.</small>
                </div>
              ) : (
                <div className="overflow-y-auto py-4 bg-white">
                  {notifications.map((notification) => {
                    const isUnread = !notification.isRead;
                    const ticketId = notification.metadata?.ticketId || notification.metadata?.ticket_id;
                    const destination = ticketNavigationPath(ticketId);

                    return (
                      <div
                        key={`modal-${notification.notificationId}`}
                        className={`px-4 py-3 border-b ${isUnread ? 'bg-white' : 'bg-white'}`}
                      >
                        <div className="flex items-start justify-between gap-6">
                          <div className="flex-1">
                            <div className="flex items-center gap-4 mb-2">
                              <span className={`px-4 py-1 rounded-full text-sm font-bold ${isUnread ? 'bg-purple-500 text-white' : 'bg-gray-600 text-white'}`}>
                                {isUnread ? 'Unread' : 'Read'}
                              </span>
                              <small className="text-gray-800">{formatRelativeTime(notification.createdAt)}</small>
                            </div>
                            <h6 className="text-xl font-bold text-gray-800 mb-2">{notification.title}</h6>
                            <p className="text-gray-800 mb-2" style={{ whiteSpace: 'pre-line' }}>{notification.body}</p>
                            {destination && (
                              <button
                                onClick={() => {
                                  handleNotificationClick(notification);
                                  closeModal();
                                }}
                                className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-gray-800 font-bold py-2 px-5 rounded-xl transition-all"
                              >
                                View ticket
                                <FaExternalLinkAlt />
                              </button>
                            )}
                          </div>
                          <div className="flex flex-col gap-3">
                            <button
                              onClick={() => isUnread ? handleMarkRead(notification.notificationId) : handleMarkUnread(notification.notificationId)}
                              className={`px-5 py-2 rounded-xl font-bold transition-all ${isUnread ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-gray-700 hover:bg-gray-600 text-gray-300'}`}
                            >
                              {isUnread ? 'Mark as read' : 'Mark as unread'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-4 sm:p-6 bg-gray-100 border-t border-gray-200">
              <div className="text-purple-600 font-semibold">Total notifications: {notifications.length}</div>
              <button
                onClick={closeModal}
                className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm flex items-center justify-center gap-2 px-6 py-2"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;